import type React from 'react'
import { useState } from 'react'
import { Button } from '../Common/Button'
import { Card } from '../Common/Card'
import { PrintSheet } from '../Common/PrintSheet'
import { JoinCodeSlips } from './JoinCodeSlips'
import { useNls } from '../../nls/nlsProvider'

export type JoinCodeOnceItem = {
  key: string
  displayName: string
  subtitle?: string
  code: string
  pin: string
  expiresAtMs: number
}

export type JoinCodesOncePanelProps = {
  items: JoinCodeOnceItem[]
  onClear: () => void
  workspaceName?: string
  panelTestId?: string
  title?: string
  notice?: string
  doneLabel?: string
  copyLabel?: string
  copiedLabel?: string
  printLabel?: string
  whatsappNumberE164?: string | null
}

export const JoinCodesOncePanel: React.FC<JoinCodesOncePanelProps> = ({
  items,
  onClear,
  workspaceName = '',
  panelTestId = 'join-codes-once-panel',
  title,
  notice,
  doneLabel,
  copyLabel,
  copiedLabel,
  printLabel,
  whatsappNumberE164,
}) => {
  const { t } = useNls()
  const [copiedKey, setCopiedKey] = useState<string | null>(null)
  const [isPrintSheetOpen, setIsPrintSheetOpen] = useState(false)

  if (items.length === 0) return null

  const handleCopyCode = async (item: JoinCodeOnceItem) => {
    const text = `${item.displayName}: Code ${item.code} PIN: ${item.pin}`
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
        setCopiedKey(item.key)
        setTimeout(() => setCopiedKey(null), 2000)
      }
    } catch (e) {
      console.warn('[JoinCodesOncePanel] Copy failed', e)
    }
  }

  return (
    <>
      <div data-testid={panelTestId}>
        <Card
          className="learnersCodesPanel"
        >
        <div className="learnersCodesPanel__header">
          <h3>{title ?? t('NLSJoinCodesOncePanelTitle')}</h3>
          <p className="learnersCodesPanel__notice">
            {notice ?? t('NLSJoinCodesOncePanelNotice')}
          </p>
        </div>
        <div className="learnersCodesPanel__list">
          {items.map((item) => (
            <div key={item.key} className="learnersCodesPanel__item">
              <strong>{item.displayName}</strong>
              {item.subtitle && (
                <span className="learnersCodesPanel__subtitle" data-testid="code-subtitle">
                  {item.subtitle}
                </span>
              )}
              <span>{item.code}</span>
              <span className="learnersCodesPanel__pin">{item.pin}</span>
              <span>
                {t('NLSJoinCodesExpiresAt', {
                  date: new Date(item.expiresAtMs).toLocaleDateString(),
                })}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void handleCopyCode(item)}
              >
                {copiedKey === item.key
                  ? (copiedLabel ?? t('NLSJoinCodesCopied'))
                  : (copyLabel ?? t('NLSJoinCodesCopy'))}
              </Button>
            </div>
          ))}
        </div>
        <div className="learnersCodesPanel__actions">
          <Button
            variant="secondary"
            size="sm"
            data-testid="codes-panel-print-btn"
            onClick={() => setIsPrintSheetOpen(true)}
          >
            {printLabel ?? t('NLSJoinCodesPrintSlips')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            data-testid="codes-panel-done-btn"
            onClick={() => {
              onClear()
              setIsPrintSheetOpen(false)
            }}
          >
            {doneLabel ?? t('NLSJoinCodesDone')}
          </Button>
        </div>
      </Card>
    </div>

      {isPrintSheetOpen && (
        <PrintSheet
          open={isPrintSheetOpen}
          onClose={() => setIsPrintSheetOpen(false)}
        >
          <JoinCodeSlips
            slips={items}
            workspaceName={workspaceName}
            whatsappNumberE164={whatsappNumberE164}
          />
        </PrintSheet>
      )}
    </>
  )
}
